/* Global fetch interceptor: handles 401 and 402.
 *
 * 401 → optionally redirect to /login (unless we're already on an auth page)
 * 402 → open the upgrade modal with the structured `detail` from the server
 *
 * Install once at app boot via `installFetchInterceptor()`. After install,
 * any module's `fetch(...)` benefits without code changes.
 */

import { setUser } from './state';

const AUTH_PATHS = new Set([
  '/login.html',
  '/register.html',
  '/forgot.html',
  '/reset.html',
  '/verify-email.html',
]);

let _upgradeHandler: ((detail: any) => void) | null = null;

/** Module that owns the upgrade modal registers itself here. */
export function setUpgradeHandler(fn: (detail: any) => void): void {
  _upgradeHandler = fn;
}

export function installFetchInterceptor(opts: { autoRedirect401?: boolean } = {}): void {
  const autoRedirect401 = opts.autoRedirect401 !== false;
  const orig = window.fetch.bind(window);

  window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
    const res = await orig(input, init);

    // Only handle our /api/* responses
    const url = typeof input === 'string' ? input
              : input instanceof URL ? input.pathname
              : input.url;
    if (!url.startsWith('/api/') && !url.includes('://')) {
      // ignore: not a known auth-protected path
    }

    if (res.status === 401 && autoRedirect401 && !AUTH_PATHS.has(location.pathname)) {
      // Clone before reading so callers can still read the body
      const clone = res.clone();
      try {
        const body = await clone.json();
        // /me returning 401 = logged out
        if (typeof body?.detail === 'string' && body.detail.toLowerCase().includes('not authenticated')) {
          setUser(null);
          // Soft redirect: queue, let the caller see the 401 first
          queueMicrotask(() => {
            const next = encodeURIComponent(location.pathname + location.search);
            location.href = `/login.html?next=${next}`;
          });
        }
      } catch { /* not JSON, skip */ }
    }

    if (res.status === 402 && _upgradeHandler) {
      const clone = res.clone();
      try {
        const body = await clone.json();
        const detail = body?.detail;
        if (detail && typeof detail === 'object' && detail.code === 'FEATURE_GATED') {
          _upgradeHandler(detail);
        }
      } catch { /* swallow */ }
    }

    return res;
  };
}
